import { type ExcalidrawElement } from '@excalidraw/excalidraw/types/element/types';
import { generateKeyBetween, generateNKeysBetween } from 'fractional-indexing';

type Mutable<T> = {
    -readonly [P in keyof T]: T[P];
};

type SortableExcalidrawElement = Pick<ExcalidrawElement, 'id' | 'type'> &
    Pick<Mutable<ExcalidrawElement>, 'customData' | 'version'>;

export type MoveOperation = <T extends SortableExcalidrawElement>(moveElements: T[], allElements: T[]) => T[];

const excludeElementTypes = ['selection'];

/**
 * Sort elements by fractional index.
 * @param elements
 */
export const sortElements = <T extends SortableExcalidrawElement>(elements: T[]) =>
    elements.sort((a, b) => {
        if (a.customData?.isBackground) {
            return -1;
        }
        if (b.customData?.isBackground) {
            return 1;
        }
        if (a.customData?.is_template && !b.customData?.is_template) {
            return -1;
        }
        if (!a.customData?.is_template && b.customData?.is_template) {
            return 1;
        }
        if (!a.customData?.sortKey) return 1;
        if (!b.customData?.sortKey) return -1;

        if (a.customData?.sortKey < b.customData?.sortKey) {
            return -1;
        }
        if (a.customData?.sortKey > b.customData?.sortKey) {
            return 1;
        }

        return 0;
    });

/**
 * Given a sorted array of elements, get the next highest key for any given element by index.
 * Starting element must have a sort key.
 * @param sortedElements
 * @param startIndex
 */
export const getNextKey = (sortedElements: SortableExcalidrawElement[], startIndex: number): string | undefined => {
    let i = startIndex + 1;
    while (sortedElements[i]?.customData?.sortKey) {
        if (sortedElements[i]?.customData?.sortKey >= sortedElements[startIndex].customData?.sortKey) {
            return sortedElements[i].customData?.sortKey;
        }
        // eslint-disable-next-line no-plusplus
        i++;
    }
    return undefined;
};

/**
 * Adds indexes where missing, ensures no duplicates. Returns sorted list of elements.
 * @param elements
 */
export const reindexElements = <T extends SortableExcalidrawElement>(elements: T[]) => {
    sortElements(elements).forEach((element, index, array) => {
        if (excludeElementTypes.includes(element.type)) {
            return;
        }
        // Skip elements that already have an index.
        if (!element.customData?.sortKey || element.customData.sortKey === array[index + 1]?.customData?.sortKey) {
            // This element must be at the end
            const prev = array[index - 1]?.customData?.sortKey ?? null;
            const next = getNextKey(array, index) ?? null;
            element.customData = { ...element.customData, sortKey: generateKeyBetween(prev, next) };
            element.version += 1;
        }
    });
    return elements;
};

/**
 * Move some elements between two indexes.
 * @param moveElements
 * @param allElements
 * @param betweenA
 * @param betweenB
 */
const move = <T extends SortableExcalidrawElement>(
    moveElements: T[],
    allElements: T[],
    betweenA: string | null | undefined,
    betweenB: string | null | undefined
) => {
    if (allElements.length < 1) {
        return allElements;
    }
    const newKeys = generateNKeysBetween(betweenA, betweenB, moveElements.length);
    const moveElementsWithKeys = reindexElements(
        sortElements(moveElements).map((e, index) => ({
            ...e,
            customData: { ...e.customData, sortKey: newKeys[index] },
            version: e.version + 1
        }))
    );
    return sortElements(allElements.map((e) => ({ ...e, ...moveElementsWithKeys.find((e2) => e2.id === e.id) })));
};

export const moveToBack: MoveOperation = <T extends SortableExcalidrawElement>(moveElements: T[], allElements: T[]) =>
    move(moveElements, allElements, null, sortElements(allElements)[0].customData?.sortKey);

export const moveToFront: MoveOperation = <T extends SortableExcalidrawElement>(moveElements: T[], allElements: T[]) =>
    move(moveElements, allElements, sortElements(allElements)[allElements.length - 1].customData?.sortKey, null);

export const moveBackward: MoveOperation = <T extends SortableExcalidrawElement>(
    moveElements: T[],
    allElements: T[]
) => {
    // Move based on the most backward element.
    const leadElement = sortElements(moveElements)[0];
    const sortedElements = sortElements(allElements);
    const moveBehindThisIndex = sortedElements.findIndex((e) => e.id === leadElement.id) - 1;
    const moveInfrontOfThisIndex = moveBehindThisIndex - 1;

    const betweenA = allElements[moveInfrontOfThisIndex]?.customData?.sortKey ?? null;
    const betweenB = allElements[moveBehindThisIndex]?.customData?.sortKey ?? null;

    if (!betweenA && !betweenB) {
        return allElements;
    }

    return move(moveElements, allElements, betweenA, betweenB);
};

export const moveForward: MoveOperation = <T extends SortableExcalidrawElement>(
    moveElements: T[],
    allElements: T[]
) => {
    // Move based on the most forward element.
    const leadElement = sortElements(moveElements)[moveElements.length - 1];
    const sortedElements = sortElements(allElements);
    const moveInfrontOfThisIndex = sortedElements.findIndex((e) => e.id === leadElement.id) + 1;
    const moveBehindThisIndex = moveInfrontOfThisIndex + 1;

    const betweenA = allElements[moveInfrontOfThisIndex]?.customData?.sortKey ?? null;
    const betweenB = allElements[moveBehindThisIndex]?.customData?.sortKey ?? null;

    if (!betweenA && !betweenB) {
        return allElements;
    }

    return move(moveElements, allElements, betweenA, betweenB);
};
